import { BidParamsMapping } from '@pubstack/common/src/bid-params-mapping'
import { BidderSeat, isCrossInventoryServerSeat } from '@pubstack/common/src/bidder-seat'
import { useCallback, useMemo } from 'react'
import { useAllBidParamsMappings } from '~/api/bid-params-mappings.api'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'

/**
 * Every seat whose rows a One media's Mapping tab edits, and those rows.
 *
 * One's own seats, client-side and legacy server-side, and the Node seats that bid on the whole inventory:
 * server-side, Spark Demand and the publisher half of Spark for Pubs. The server-side setup of a One media
 * is the server-side variant of its One stacks (ADR-0036), so all of it is edited here. Pubstack's
 * scope-level seats are left out: their rows name no media.
 *
 * Each seat keeps its product, and its rows are read from and written to that product's API. The Node
 * API refuses a demo account, so a demo account sees One's seats only.
 */
export const useMediaMappingSeats = (siteId: string) => {
  const user = useAuthenticatedUser()
  const { data: admSeats, isLoading: isLoadingAdmSeats } = useBidderSeats('adm')
  const { data: pbsSeats, isLoading: isLoadingPbsSeats } = useBidderSeats('pbs', undefined, user.getScopeRole() !== 'demo')
  const nodeSeats = useMemo(() => pbsSeats.filter(isCrossInventoryServerSeat), [pbsSeats])

  const admMappings = useAllBidParamsMappings(
    'adm',
    admSeats.map((seat) => seat.id),
    siteId
  )
  const nodeMappings = useAllBidParamsMappings(
    'pbs',
    nodeSeats.map((seat) => seat.id),
    siteId
  )
  const { refetch: refetchAdmMappings } = admMappings
  const { refetch: refetchNodeMappings } = nodeMappings

  const bidderSeats = useMemo<BidderSeat[]>(() => [...admSeats, ...nodeSeats], [admSeats, nodeSeats])
  const existingMappings = useMemo<BidParamsMapping[]>(() => [...admMappings.data, ...nodeMappings.data], [admMappings.data, nodeMappings.data])
  const refetchMappings = useCallback(() => Promise.all([refetchAdmMappings(), refetchNodeMappings()]), [refetchAdmMappings, refetchNodeMappings])

  return {
    bidderSeats,
    existingMappings,
    refetchMappings,
    isLoading: isLoadingAdmSeats || isLoadingPbsSeats || admMappings.isLoading || nodeMappings.isLoading,
  }
}
